import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { isArray, isEmpty } from "lodash";
import { useDocumentsAPI } from "./useDocumentsAPI";
import { convertDocumentData } from "./documentUtils";
import DocumentSection from "./DocumentSection";
import { updateStepMappingToCompleted } from "../../utils/stepMappingUtils";

const DocumentsManagement = ({ readOnly = false }) => {
  const { userData, token } = useSelector((state) => state.auth);
  const [documentList, setDocumentList] = useState([]);
  const [selectedDocId, setSelectedDocId] = useState(0);
  const [file, setFile] = useState(null);

  const {
    loading,
    isCalled,
    getDocumentsList,
    uploadDocument,
    deleteDocument,
    resetIsCalled
  } = useDocumentsAPI(userData);

  // Memoize the converted document data to avoid recalculation on every render
  const convertedDocumentList = useMemo(() => {
    return convertDocumentData(documentList);
  }, [documentList]);

  // Optimized useEffect - consolidated the two useEffects into one
  useEffect(() => {
    if (userData?.id) {
      if (isCalled) {
        // Reset isCalled after API call
        resetIsCalled();
      }
      fetchDocuments();
    }
  }, [userData?.id, isCalled, resetIsCalled]);

  const fetchDocuments = useCallback(async () => {
    const documents = await getDocumentsList();
    setDocumentList(documents);
  }, [getDocumentsList]);

  const handleUpload = useCallback((id) => {
    setSelectedDocId(id);
  }, []);

  const handleFileUpload = useCallback(async (file, docId = null) => {
    const documentId = docId || selectedDocId;
    if (!documentId) {
      return;
    }
    
    const success = await uploadDocument(file, documentId);
    if (success) {
      setSelectedDocId(0);
      setFile(null);
      
      // Refresh documents list to get updated data
      const updatedDocuments = await getDocumentsList();
      setDocumentList(updatedDocuments);
      
      // Check if the uploaded document was mandatory and if all mandatory docs are now complete
      const uploadedDoc = updatedDocuments.find(doc => doc.id === documentId);
      if (uploadedDoc?.is_mandatory) {
        const mandatoryDocs = updatedDocuments.filter(doc => doc.is_mandatory);
        const allMandatoryCompleted = mandatoryDocs.every(doc => doc.url && doc.url.trim() !== '');
        
        if (allMandatoryCompleted) {
          // Call step mapping API to mark DOCUMENT_SUBMIT as COMPLETED
          await updateStepMappingToCompleted(userData.id, "DOCUMENT_SUBMIT", token);
        }
      }
    }
  }, [selectedDocId, uploadDocument, getDocumentsList, userData?.id, token]);

  const handleDelete = useCallback(async (id) => {
    await deleteDocument(id);
  }, [deleteDocument]);


  return (
    <Box sx={{ p: 2 }}>
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      
      {isArray(convertedDocumentList) && !isEmpty(convertedDocumentList) ? (
        <>
          {convertedDocumentList.map((item, index) => (
            <DocumentSection
              key={`${item.type}-${index}`}
              title={item?.type}
              documents={item?.sub_data}
              onUpload={readOnly ? undefined : handleFileUpload}
              onDelete={readOnly ? undefined : handleDelete}
              readOnly={readOnly}
            />
          ))}
        </>
      ) : (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: 200,
          textAlign: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 500, color: '#6B7280' }}>
            Loading...
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DocumentsManagement;